import { useMemo, useState } from 'react'
import { Calendar, Clock, Users, TrendingUp } from 'lucide-react'
import { formatCurrency, formatDateForUI, formatTimeForUI, getDayOfWeek, getDayOfWeekName } from '@meiuca/web-foundation'
import { modalitiesManager } from '@/managers/modalitiesManager'
import { DayUseOccurrenceDetailModal } from '@/components/DayUseOccurrenceDetailModal'
import type { VenueDetailUpcomingOccurrencesComponent, UpcomingOccurrenceItem } from '../../../types/sdui-arena-detail'

interface VenueDetailUpcomingOccurrencesProps {
  config: VenueDetailUpcomingOccurrencesComponent
}

const getDayOfWeekLabel = (dateTimeString: string): string => {
  const localDate = new Date(dateTimeString)
  return getDayOfWeekName(getDayOfWeek(localDate))
}

export function VenueDetailUpcomingOccurrences({ config }: VenueDetailUpcomingOccurrencesProps) {
  const { title, modalities } = config
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string | null>(null)

  const groupedByModality = useMemo(() => {
    return modalities.map(modalityGroup => ({
      modality: modalityGroup.modality,
      modalityLabel: modalitiesManager.getModalityDescription(modalityGroup.modality),
      occurrences: [...modalityGroup.occurrences].sort(
        (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
      )
    }))
  }, [modalities])

  const [activeModality, setActiveModality] = useState<string | null>(null)

  const modalityOptions = useMemo(() => groupedByModality.map(group => group.modality), [groupedByModality])

  const selectedModality = useMemo(() => {
    const fallback = modalityOptions[0] ?? null
    if (!activeModality || !modalityOptions.includes(activeModality)) {
      return fallback
    }
    return activeModality
  }, [activeModality, modalityOptions])

  const selectedModalityGroup = useMemo(() => {
    return groupedByModality.find(group => group.modality === selectedModality) ?? null
  }, [groupedByModality, selectedModality])

  if (!selectedModalityGroup || selectedModalityGroup.occurrences.length === 0) {
    return null
  }

  const renderOccurrence = (occurrence: UpcomingOccurrenceItem) => {
    const isUnlimited = occurrence.maxPeople === null || occurrence.maxPeople === undefined
    const occupancyPercentage = !isUnlimited && occurrence.maxPeople
      ? Math.round((occurrence.totalParticipants / occurrence.maxPeople) * 100)
      : null

    const isAlmostFull = occupancyPercentage !== null && occupancyPercentage >= 80
    const isFull = !isUnlimited && occurrence.availableSpots === 0

    return (
      <div
        key={occurrence.id}
        onClick={() => setSelectedOccurrenceId(occurrence.id)}
        className="group relative overflow-hidden rounded-lg border border-border-light bg-white hover:border-brand-primary hover:shadow-md transition-all duration-200 cursor-pointer"
      >
        <div className="p-4">
          {/* Data e Dia da Semana */}
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-brand-primary" />
            <div>
              <div className="text-sm font-semibold text-text-primary">
                {formatDateForUI(occurrence.startsAt, 'medium')}
              </div>
              <div className="text-xs text-text-secondary capitalize">
                {getDayOfWeekLabel(occurrence.startsAt)}
              </div>
            </div>
          </div>

          {/* Horário */}
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-text-secondary" />
            <div className="text-sm text-text-primary">
              {formatTimeForUI(occurrence.startsAt)} - {formatTimeForUI(occurrence.endsAt)}
            </div>
          </div>

          {/* Preço e Participantes */}
          <div className="flex items-center justify-between gap-4 mb-3 pb-3 border-b border-border-light">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-text-secondary" />
              <span className="text-sm text-text-secondary">
                {occurrence.totalParticipants} pessoa{occurrence.totalParticipants === 1 ? '' : 's'}
                {isUnlimited ? ' (ilimitado)' : ` / ${occurrence.maxPeople ?? occurrence.minPeople}`}
              </span>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-brand-primary">
                {formatCurrency(occurrence.pricePerPersonCents)}
              </div>
              <div className="text-xs text-text-secondary">por pessoa</div>
            </div>
          </div>

          {/* Status de Vagas */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className={`h-4 w-4 ${
                isUnlimited
                  ? 'text-blue-500'
                  : isFull
                  ? 'text-red-500'
                  : isAlmostFull
                  ? 'text-orange-500'
                  : 'text-green-500'
              }`} />
              <span className={`text-sm font-medium ${
                isUnlimited
                  ? 'text-blue-600'
                  : isFull
                  ? 'text-red-600'
                  : isAlmostFull
                  ? 'text-orange-600'
                  : 'text-green-600'
              }`}>
                {isUnlimited
                  ? 'Vagas ilimitadas'
                  : isFull
                  ? 'Lotado'
                  : `${occurrence.availableSpots} ${occurrence.availableSpots === 1 ? 'vaga' : 'vagas'}`}
              </span>
            </div>

            {/* Badge de ocupação */}
            {occupancyPercentage !== null ? (
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                isFull
                  ? 'bg-red-100 text-red-700'
                  : isAlmostFull
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                {occupancyPercentage}% ocupado
              </div>
            ) : (
              <div className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                Sem limite
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <section>
        <h2 className="text-xl font-bold text-text-primary mb-4">{title}</h2>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {groupedByModality.map(group => (
              <button
                key={group.modality}
                type="button"
                onClick={() => setActiveModality(group.modality)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  selectedModality === group.modality
                    ? 'bg-brand-primary text-white border-brand-primary shadow-sm'
                    : 'text-text-secondary border-border-light hover:text-text-primary hover:border-border-strong bg-white'
                }`}
              >
                {group.modalityLabel}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedModalityGroup.occurrences.map(occurrence => renderOccurrence(occurrence))}
          </div>
        </div>
      </section>

      <DayUseOccurrenceDetailModal
        occurrenceId={selectedOccurrenceId}
        isOpen={!!selectedOccurrenceId}
        onClose={() => setSelectedOccurrenceId(null)}
      />
    </>
  )
}
